/**
 * Result display component.
 * Renders VerifyResult as structured HTML.
 */

import type { VerifyResult, DecodedTransaction, DecodedField, Warning } from '@txviz/core';

export function renderResult(result: VerifyResult): void {
  const section = document.getElementById('result-section')!;
  const errorSection = document.getElementById('error-section')!;

  // Hide error, show result
  errorSection.classList.add('hidden');
  section.classList.remove('hidden');

  let html = '';

  // Status banner
  const bannerClass = result.verified ? 'verified' : 'failed';
  const statusIcon = result.verified ? '\u2705' : '\ud83d\udea8';
  const statusText = result.verified
    ? 'Transaction Verified'
    : 'Hash Mismatch \u2014 DO NOT SIGN';
  const summaryText = result.verified && result.decoded
    ? escapeHtml(result.decoded.summary)
    : 'The transaction data does not match the hash you are being asked to sign.';

  html += `
    <div class="result-banner ${bannerClass}">
      <div class="status">${statusIcon} ${statusText}</div>
      <div class="summary">${summaryText}</div>
    </div>
  `;

  // Hash comparison
  const hashClass = result.verified ? 'match' : 'mismatch';
  html += `
    <div class="hash-compare">
      <div class="hash-row">
        <span class="hash-label">Computed</span>
        <span class="hash-value ${hashClass}">${escapeHtml(result.computedHash)}</span>
      </div>
      <div class="hash-row">
        <span class="hash-label">Expected</span>
        <span class="hash-value ${hashClass}">${escapeHtml(result.expectedHash)}</span>
      </div>
    </div>
  `;

  // Warnings
  if (result.warnings.length > 0) {
    html += '<div class="warnings-section">';
    for (const warning of result.warnings) {
      html += renderWarning(warning);
    }
    html += '</div>';
  }

  // Decoded fields — shown for both verified and unverified transactions
  if (result.decoded) {
    // If not verified, show an unverified banner above the decoded fields
    if (!result.verified) {
      html += `
        <div class="unverified-banner">
          <div class="unverified-title">\u26a0\ufe0f Unverified Transaction Details</div>
          <div class="unverified-description">
            The following details are decoded from the provided transaction data, but the hash does NOT match.
            This data is <strong>unverified</strong> and could be different from what you would actually sign.
          </div>
        </div>
      `;
    }

    html += `
      <div class="fields-table ${!result.verified ? 'unverified' : ''}">
        <div class="fields-header">${escapeHtml(result.decoded.type)}</div>
        ${result.decoded.fields.map(renderField).join('')}
      </div>
    `;

    // Raw hex toggle
    if (result.decoded.rawHex) {
      const rawId = 'raw-hex-' + Date.now();
      html += `
        <button class="raw-hex-toggle" onclick="
          var el = document.getElementById('${rawId}');
          el.classList.toggle('hidden');
          this.textContent = el.classList.contains('hidden') ? 'Show Raw Hex' : 'Hide Raw Hex';
        ">Show Raw Hex</button>
        <div id="${rawId}" class="raw-hex-content hidden">
          ${escapeHtml(result.decoded.rawHex)}
        </div>
      `;
    }
  }

  section.innerHTML = html;
}

/**
 * Render a decoded transaction without verification (Solana decode-only mode).
 * No hash comparison, no verified/unverified status — just the decoded fields.
 */
export function renderDecoded(decoded: DecodedTransaction): void {
  const section = document.getElementById('result-section')!;
  const errorSection = document.getElementById('error-section')!;

  errorSection.classList.add('hidden');
  section.classList.remove('hidden');

  let html = `
    <div class="result-banner verified">
      <div class="status">\ud83d\udd0d Decoded Transaction</div>
      <div class="summary">${escapeHtml(decoded.summary)}</div>
    </div>
  `;

  html += `
    <div class="fields-table">
      <div class="fields-header">${escapeHtml(decoded.type)}</div>
      ${decoded.fields.map(renderField).join('')}
    </div>
  `;

  if (decoded.rawHex) {
    const rawId = 'raw-hex-' + Date.now();
    html += `
      <button class="raw-hex-toggle" onclick="
        var el = document.getElementById('${rawId}');
        el.classList.toggle('hidden');
        this.textContent = el.classList.contains('hidden') ? 'Show Raw Hex' : 'Hide Raw Hex';
      ">Show Raw Hex</button>
      <div id="${rawId}" class="raw-hex-content hidden">
        ${escapeHtml(decoded.rawHex)}
      </div>
    `;
  }

  section.innerHTML = html;
}

export function renderError(title: string, message: string, detail?: string): void {
  const section = document.getElementById('error-section')!;
  const resultSection = document.getElementById('result-section')!;

  resultSection.classList.add('hidden');
  section.classList.remove('hidden');

  let html = `
    <div class="error-box">
      <h3>${escapeHtml(title)}</h3>
      <p>${escapeHtml(message)}</p>
  `;

  if (detail) {
    html += `<pre>${escapeHtml(detail)}</pre>`;
  }

  html += '</div>';
  section.innerHTML = html;
}

export function hideResults(): void {
  document.getElementById('result-section')!.classList.add('hidden');
  document.getElementById('error-section')!.classList.add('hidden');
}

function renderField(field: DecodedField): string {
  let valueClass = 'field-value';
  if (field.type === 'address') valueClass += ' address';
  if (field.type === 'value') valueClass += ' value-type';

  let html = `
    <div class="field-row">
      <span class="field-label">${escapeHtml(field.label)}</span>
      <div>
        <div class="${valueClass}">${escapeHtml(field.value)}</div>
  `;

  if (field.warning) {
    html += `<div class="field-warning">\u26a0 ${escapeHtml(field.warning)}</div>`;
  }

  html += '</div></div>';
  return html;
}

function renderWarning(warning: Warning): string {
  const icons: Record<string, string> = {
    info: '\u2139\ufe0f',
    warning: '\u26a0\ufe0f',
    critical: '\ud83d\udea8',
  };
  const icon = icons[warning.severity] ?? '\u26a0\ufe0f';
  return `
    <div class="warning-callout ${warning.severity}">
      ${icon} ${escapeHtml(warning.message)}
    </div>
  `;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
